import React, { useState, useEffect } from 'react';
import { 
  Mail, MailOpen, Trash2, Loader2, User, Building2, Phone, 
  Calendar, ChevronDown, ChevronUp, X, ExternalLink
} from 'lucide-react';
import { contactApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await contactApi.getSubmissions();
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id, currentRead) => {
    try {
      await contactApi.markRead(id, !currentRead);
      setSubmissions(submissions.map(s => 
        s.id === id ? { ...s, read: !currentRead } : s
      ));
    } catch (error) {
      console.error('Error updating submission:', error);
    }
  };

  const handleDelete = async () => {
    if (!submissionToDelete) return;
    setDeleting(true);
    try {
      await contactApi.delete(submissionToDelete.id);
      setSubmissions(submissions.filter(s => s.id !== submissionToDelete.id));
      setSubmissionToDelete(null);
      if (expandedId === submissionToDelete.id) {
        setExpandedId(null);
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
    } finally {
      setDeleting(false);
    }
  };

  const toggleExpand = async (submission) => {
    if (expandedId === submission.id) {
      setExpandedId(null);
    } else {
      setExpandedId(submission.id);
      // Mark as read when expanding
      if (!submission.read) {
        await handleMarkRead(submission.id, false);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = submissions.filter(s => !s.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-contact-submissions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contact Submissions</h1>
          <p className="text-gray-500 mt-1">
            {submissions.length} total submissions
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No contact submissions yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Submissions from your contact form will appear here
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm divide-y">
          {submissions.map((submission) => (
            <div key={submission.id} className="hover:bg-gray-50 transition-colors">
              {/* Summary Row */}
              <div 
                className="flex items-center p-4 cursor-pointer"
                onClick={() => toggleExpand(submission)}
              >
                {/* Read/Unread indicator */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkRead(submission.id, submission.read);
                  }}
                  className={`p-2 rounded-full mr-3 ${
                    submission.read 
                      ? 'text-gray-400 hover:bg-gray-100' 
                      : 'text-orange-500 bg-orange-50 hover:bg-orange-100'
                  }`}
                  title={submission.read ? 'Mark as unread' : 'Mark as read'}
                >
                  {submission.read ? (
                    <MailOpen className="h-5 w-5" />
                  ) : (
                    <Mail className="h-5 w-5" />
                  )}
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${submission.read ? 'text-gray-700' : 'text-gray-900'}`}>
                      {submission.name}
                    </span>
                    {submission.company && (
                      <span className="text-sm text-gray-500">
                        ({submission.company})
                      </span>
                    )}
                  </div>
                  <p className={`text-sm truncate ${submission.read ? 'text-gray-500' : 'text-gray-700 font-medium'}`}>
                    {submission.subject}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(submission.submitted_at)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubmissionToDelete(submission);
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {expandedId === submission.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === submission.id && (
                <div className="px-4 pb-4 bg-gray-50 border-t">
                  <div className="pt-4 space-y-4">
                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{submission.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <a 
                          href={`mailto:${submission.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {submission.email}
                        </a>
                      </div>
                      {submission.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <a 
                            href={`tel:${submission.phone}`}
                            className="text-blue-600 hover:underline"
                          >
                            {submission.phone}
                          </a>
                        </div>
                      )}
                      {submission.company && (
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">{submission.company}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{formatDate(submission.submitted_at)}</span>
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Subject
                      </h4>
                      <p className="text-gray-900 font-medium">{submission.subject}</p>
                    </div>

                    {/* Message */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Message
                      </h4>
                      <div className="bg-white p-4 rounded-lg border text-gray-700 whitespace-pre-wrap">
                        {submission.message}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-3 pt-2">
                      <a
                        href={`mailto:${submission.email}?subject=Re: ${submission.subject}`}
                        className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Reply via Email
                      </a>
                      <Button
                        variant="outline"
                        onClick={() => handleMarkRead(submission.id, submission.read)}
                      >
                        {submission.read ? (
                          <><Mail className="h-4 w-4 mr-2" />Mark Unread</>
                        ) : (
                          <><MailOpen className="h-4 w-4 mr-2" />Mark Read</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!submissionToDelete} onOpenChange={() => setSubmissionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the submission from <strong>{submissionToDelete?.name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                'Delete Submission'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminContactSubmissions;
